import { Suspense, useEffect, useTransition } from 'react'

import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { useUser } from '@clerk/react'

import { isDemoPresentation } from '@/lib/constants'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { Section, SectionTitle } from '@/features/app-shell/section'
import { DemoHidden } from '@/features/demo/components/demo-hidden'
import { integrationQueries } from '@/features/integrations/queries'
import {
	TaskList,
	TaskListSkeleton,
} from '@/features/onboarding/components/task-list'
import { useHomeRedirect } from '@/features/onboarding/hooks/use-home-redirect'

import { userQueries } from '@/shared/auth/queries'
import {
	getCurrentTenant,
	useCurrentTenant,
	useIsOwnerTenant,
} from '@/shared/auth/store'
import { tenantSessionQueries } from '@/shared/auth/tenant-session'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { TableSkeleton } from '@/components/skeletons/table-skeleton'

export const Route = createFileRoute('/_dashboard/home')({
	// Re-run this loader on an in-page tenant switch. Such a switch changes only
	// `search.t` (path and params stay), and TanStack Router reuses a cached
	// loader result unless params or `loaderDeps` change — so without this dep
	// the loader never re-runs and the new tenant's data is never primed
	// (GOTCHAS, "Child route loader skips its guard on in-page tenant switch").
	// Benign today: the parent `_dashboard.beforeLoad` clears the query cache on
	// every switch, and this page reads reactive tenant hooks rather than the
	// loader's return value. Declared so the guarantee is local, not borrowed.
	loaderDeps: ({ search }) => ({ t: search.t }),

	loader: async ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		if (tenant) {
			Promise.all([
				queryClient.ensureQueryData(tenantSessionQueries.details(tenant)),
				queryClient.prefetchQuery(integrationQueries.list(tenant)),
			])
		}

		// Returning makes the route wait for the queries to complete before rendering the component
		return await queryClient.ensureQueryData(userQueries.me())
	},

	head: () => ({
		meta: [
			{
				title: 'Home | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	errorComponent: RouteErrorComponent,
	pendingComponent: RoutePendingComponent,
})

function RouteComponent() {
	const { handleRedirect } = useHomeRedirect()
	const { user, isLoaded: isClerkLoaded } = useUser()

	// Get tenant without assertion - may be undefined during skeleton phase
	const tenant = useCurrentTenant()

	// Only fetch tenant details when tenant is available
	const { isLoading: isTenantLoading } = useQuery({
		...tenantSessionQueries.details(tenant ?? ''),
		enabled: !!tenant,
	})

	const isOwnerTenant = useIsOwnerTenant()

	const [isPending, startTransition] = useTransition()

	useEffect(() => {
		if (user && tenant && !isTenantLoading) {
			// We check if quick start is completed to avoid redirecting to the
			// onboarding page. The demo never onboards (integration setup is a
			// write flow it hides) and /home is also where every
			// excluded route bounces to — so it always redirects straight on.
			if (
				isDemoPresentation ||
				user.unsafeMetadata.onboarding_progress?.quickStart.completed ||
				!isOwnerTenant
			) {
				startTransition(() => {
					handleRedirect()
				})
			}
		}
	}, [user, tenant, handleRedirect, isOwnerTenant, isTenantLoading])

	// Show pending when tenant not yet available or still loading
	if (isPending || !isClerkLoaded || !tenant || isTenantLoading) {
		return <RoutePendingComponent />
	}

	return (
		<Section>
			<SectionTitle className="mb-0">Welcome to your BitLyft AIR®</SectionTitle>

			<p className="text-muted-foreground text-sm">
				Your unified platform to manage and secure your entire organization
				effortlessly.
			</p>

			{/* Render-time gate, not just the redirect above: the effect fires after
			    the first paint, so without this the demo flashes the Graylog
			    onboarding checklist on its way out.

			    `isOwnerTenant` is the same argument for a different rule, and it
			    needs its own render-time gate for the same reason. Onboarding
			    belongs to `auth_context.owning_tenant` and to no other Tenant, and
			    the redirect below it is an effect — so a user on a Tenant they do
			    not own painted the checklist for one frame before bouncing. The
			    two conditions are independent: the demo is a build, this is a
			    per-Tenant entitlement, and neither implies the other. */}
			{isOwnerTenant && (
				<DemoHidden>
					<ErrorBoundary
						fallbackTitle="Error loading tasks"
						fallbackMessage="Please try again later"
						feature="onboarding"
					>
						<Suspense fallback={<TableSkeleton rows={10} />}>
							<TaskList />
						</Suspense>
					</ErrorBoundary>
				</DemoHidden>
			)}
		</Section>
	)
}

function RoutePendingComponent() {
	return (
		<Section>
			<SectionTitle className="mb-0">Welcome to your BitLyft AIR®</SectionTitle>
			<p className="text-muted-foreground text-sm">
				Your unified platform to manage and secure your entire organization
				effortlessly.
			</p>

			<TaskListSkeleton />
		</Section>
	)
}
